import { AfterContentInit, Component, EventEmitter, forwardRef, Input, OnInit, Output, HostListener, ElementRef, OnChanges, SimpleChanges, ViewChild } from '@angular/core'
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { filter } from 'rxjs/operators'
import {
    AvailabilityType, BoundingBox,
    RegionCriterion,
    SearchCriteriaWhereToMenuOptionsGQL,
    SearchCriteriaWhereToMenuOptionsQuery,
    SearchRegion
} from '../../../../../graphql'
import { groupBy, UnsubscribeOnDestroy } from '../../../../utility'

const accessor: any = {
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SearchCriteriaWhereToMenuComponent)
}
const GROUP_NAME_CONTINENTS = 'Continents'
const GROUP_NAME_COUNTRIES = 'Countries'
const ALL_DESTINATIONS = 'All destinations'

@Component({
    providers: [accessor],
    selector: 'app-search-criteria-where-to-menu',
    templateUrl: 'search-criteria-where-to-menu.html'
})
export class SearchCriteriaWhereToMenuComponent extends UnsubscribeOnDestroy implements ControlValueAccessor, OnInit, AfterContentInit, OnChanges {
    @Input() includeComingSoon: boolean
    @Input() availabilityType: AvailabilityType
    @Input() activateMenuPanel = false
    @Input() inline = false
    @Input() border = true

    onChange: (regionCriterion: RegionCriterion) => void
    onTouched: () => void
    whereToControl: FormControl
    allRegionsList: SearchRegions[] = []
    savedCriterion: RegionCriterion
    writeValue$ = new BehaviorSubject(null)
    initRegionsList$ = new BehaviorSubject(null)
    menuPanelActive = false
    selectedRegionName: string
    defaultRegionName: string = ALL_DESTINATIONS

    @Output() activeRegionChanged: EventEmitter<BoundingBox> = new EventEmitter<BoundingBox>()
    @Output() forceSearch = new EventEmitter<void>()
    @Output() searchRegionNameChanged: EventEmitter<string> = new EventEmitter<string>()

    @HostListener('document:click', ['$event'])
    clickout(event: Event) {
        const targetElement = event.target as HTMLInputElement
    
        if (!this.eRef.nativeElement.contains(targetElement) && !this.activateMenuPanel) {
            this.menuPanelActive = false
        } 

        this.activateMenuPanel = false
    }

    @HostListener('document:keydown.escape', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        this.menuPanelActive = false
        this.activateMenuPanel = false 
    }

    // Constructor
    constructor(
        private _searchCriteriaWhereToOptionsGQL: SearchCriteriaWhereToMenuOptionsGQL,
        private eRef: ElementRef<HTMLElement>
    ) {
        super()
    }

    ngOnInit() {
        this.selectedRegionName = ALL_DESTINATIONS
        const nonNullWriteValue$ = this.writeValue$.pipe(filter(v => !!v))
        const nonNullInitRegionsList$ = this.initRegionsList$.pipe(filter(v => !!v))

        this._subscriptions.push(
            combineLatest([nonNullWriteValue$, nonNullInitRegionsList$])
            .subscribe(_ => this.updateInitialValue())
        )
    }

    ngAfterContentInit(): void {
        this._subscriptions.push(this._searchCriteriaWhereToOptionsGQL.watch({includeComingSoon: this.includeComingSoon, availabilityTypes: [this.availabilityType]}).valueChanges.subscribe(result => {
            if (!result.loading) {
                let regions: SearchCriteriaWhereToMenuOptionsQuery['searchOptions']['groupedRegions'][0]['regions'] = []
                const continents = result.data.searchOptions.groupedRegions
                    .find(groupRegions => groupRegions.groupName === GROUP_NAME_CONTINENTS)
                if (continents) {
                    regions = [...continents.regions]
                    regions
                        .sort((region1, region2) => region1.name.toLocaleLowerCase().localeCompare(region2.name.toLocaleLowerCase()))
                }

                const countries = result.data.searchOptions.groupedRegions
                    .find(groupRegions => groupRegions.groupName === GROUP_NAME_COUNTRIES)
                if (countries) {
                    const regionsToSubregionsMap: { [key: string]: SearchRegion[] } = groupBy(countries.regions, 'parentRegionId')
                    Object.entries(regionsToSubregionsMap).forEach(([_, value]) => {
                        value
                            .sort((region1, region2) => region1.name.toLocaleLowerCase().localeCompare(region2.name.toLocaleLowerCase()))
                    })
                    this.allRegionsList = regions.map(
                        region => ({
                            ...region,
                            subRegions: [this.defaultSubRegion(region), ...regionsToSubregionsMap[region.regionId] || []],
                        } as SearchRegions)
                    )
                }

                this.allRegionsList.unshift(this.defaultRegion())
                this.initRegionsList$.next(this.allRegionsList)
            }
        }))

        this.menuPanelActive = this.activateMenuPanel
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.activateMenuPanel?.previousValue !== changes?.activateMenuPanel?.currentValue) {
            this.menuPanelActive = changes.activateMenuPanel.currentValue
        }
    }

    private defaultSubRegion(region: SearchRegion): SearchRegion {
        return {
            name: `-- All ${region.name} --`,
            regionId: region.regionId,
            parentRegionId: null,
            boundingBox: region.boundingBox
        }
    }

    private defaultRegion(): SearchRegions {
        return {
            name: ALL_DESTINATIONS,
            regionId: null,
            subRegions: null,
            boundingBox: null
        } as SearchRegions
    }

    private updateInitialValue(): void {
        if (!this.savedCriterion.regionId) {
            return this.whereToControl.setValue(ALL_DESTINATIONS)
        }

        const initialRegion = this.allRegionsList.find(region => region.regionId === this.savedCriterion.regionId)
        if (!initialRegion) {
            return this.searchAllDestinations()
        }
        if (!this.savedCriterion.subRegionId) {
            this.selectedRegionName = `-- All ${initialRegion.name} --`
            return this.whereToControl.setValue(`-- All ${initialRegion.name} --`)
        }

        const subRegion = initialRegion.subRegions.find(region => region.regionId === this.savedCriterion.subRegionId)
        if (!subRegion) {
            return this.searchAllDestinations()
        }
        this.selectedRegionName = subRegion.name
        this.whereToControl.setValue(subRegion.name)
    }

    searchAllDestinations(): void {
        this.resetMenu()
        this.menuPanelActive = false
        this.onChange({ regionId: null })
        this.forceSearch.next()
    }

    resetMenu() {
        this.selectedRegionName = ALL_DESTINATIONS
        this.whereToControl.setValue(ALL_DESTINATIONS)
    }

    onNext(event: SearchRegion) {
        let criterion
        if (event.parentRegionId) {
            // country is selected
            criterion = { regionId: event.parentRegionId, subRegionId: event.regionId, regionName: event.name }
        } else {
            // All of a continent or All Destinations(regionId==='') is selected
            criterion = { regionId: event.regionId, regionName: event.name }
        }
        this.onChange(criterion)
        if (event.boundingBox) {
            this.activeRegionChanged.next(event.boundingBox)
        }
   
        this.selectedRegionName = event.name
        this.searchRegionNameChanged.next(this.selectedRegionName)
        this.whereToControl.setValue(event.name)
        this.menuPanelActive = false
    }

    // Public
    registerOnChange(onChange) {
        this.onChange = onChange
    }

    registerOnTouched(onTouched) {
        this.onTouched = onTouched
    }

    writeValue(regionCriterion: RegionCriterion | undefined) {
        this.savedCriterion = regionCriterion
        this.whereToControl = new FormControl(ALL_DESTINATIONS)
        this.writeValue$.next(regionCriterion)
    }
}

export type SearchRegions = SearchRegion & { subRegions: SearchRegion[] }
